/**
 * Fetches the cart's shipping rates for the current market's country from Shopify's
 * AJAX API, shows the cheapest one and adds it to the total.
 * Re-runs automatically after cart updates: cart.js replaces #cart-summary .js-contents,
 * which reconnects a fresh instance of this element.
 */
if (!customElements.get('cart-shipping-estimate')) {
  customElements.define(
    'cart-shipping-estimate',
    class CartShippingEstimate extends HTMLElement {
      // Shopify requires a postal code to return rates. Shipping zones are whole countries,
      // so any valid zip in the country gives the same rate. Add new selling markets here.
      static PLACEHOLDER_ZIPS = { DE: '10115', FR: '75001', CH: '8001' };

      connectedCallback() {
        this.estimate();
      }

      async estimate() {
        const valueElement = this.querySelector('[data-shipping-value]');
        const totalElement = this.querySelector('[data-total-price]');
        const params = new URLSearchParams({ 'shipping_address[country]': this.dataset.country });
        const placeholderZip = CartShippingEstimate.PLACEHOLDER_ZIPS[this.dataset.country];
        if (placeholderZip) params.set('shipping_address[zip]', placeholderZip);

        try {
          // The synchronous endpoint returns rates in the market's currency (CHF 15.00 on CH).
          // async_shipping_rates.json returns the shop's currency (EUR) instead.
          const response = await fetch(`${routes.cart_url}/shipping_rates.json?${params}`);
          if (!response.ok) throw new Error(`shipping_rates: ${response.status}`);

          const rates = (await response.json())?.shipping_rates || [];
          if (!this.isConnected) return;
          if (!rates.length) throw new Error('No shipping rates');

          // Never convert: if a rate isn't in the market's currency, leave it to checkout.
          const marketCurrency = window.Shopify?.currency?.active;
          if (rates.some((rate) => rate.currency && rate.currency !== marketCurrency)) {
            valueElement.textContent = this.dataset.fallbackLabel;
            return;
          }

          const shippingCents = Math.min(...rates.map((rate) => Math.round(parseFloat(rate.price) * 100)));
          valueElement.textContent = shippingCents === 0 ? this.dataset.freeLabel : this.formatMoney(shippingCents);
          totalElement.textContent = this.formatMoney(Number(this.dataset.totalCents) + shippingCents);
        } catch (error) {
          console.error(error);
          if (this.isConnected) valueElement.textContent = this.dataset.fallbackLabel;
        }
      }

      // Formats like Liquid's money filter, using a sample of 123456 cents
      // rendered by Liquid to read the market's symbol position and separators.
      formatMoney(cents) {
        const match = this.dataset.moneySample.match(/^(.*?)1(\D?)234(\D)56(.*)$/);
        if (!match) throw new Error(`Unrecognised money format: ${this.dataset.moneySample}`);

        const [, prefix, thousandsSeparator, decimalSeparator, suffix] = match;
        const integer = Math.floor(cents / 100)
          .toString()
          .replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSeparator);
        const decimals = `${decimalSeparator}${String(cents % 100).padStart(2, '0')}`;

        return `${prefix}${integer}${decimals}${suffix}`;
      }
    }
  );
}
